import {useStoreon} from "@storeon/solidjs";
import {createMemo, createSignal, Switch, Match, For, Show} from "solid-js";
import {Link, useParams} from "@solidjs/router";
import {Button, Modal} from "solid-bootstrap";
import reservationStatus from "../../helpers/reservationStatus";
import {formatDate, formatHoraire, describe, cancelErrorMessage} from "../../helpers/reservationFormat";
import {RESERVATIONS_PAGE_SIZE} from "../../store/Orders";

export default props => {
    const [state, dispatch] = useStoreon();
    const params = useParams();

    // /user/orders/<token> : lien magique de l'e-mail de confirmation. La
    // plupart des clients réservent en invité et n'ont jamais défini de mot de
    // passe : sans cette authentification ils ne pourraient pas atteindre leurs
    // réservations. Le store enchaîne authentification -> pullUser ->
    // loadMyReservations, l'ordre étant impératif (la session doit précéder la
    // liste).
    // Sans jeton : comportement habituel. On repart toujours de la première
    // page : le skip vit dans le store global et survivrait sinon à une
    // navigation.
    if (params.token) {
        dispatch("authenticateMagicLink", params.token);
    } else {
        dispatch("clearMagicLinkError");
        dispatch("pullUser");
        dispatch("loadMyReservations", { skip: 0, refetch: true });
    }
    // cancelReservationResult vit dans le store global : sans ce nettoyage, une
    // annulation échouée laisse sa bannière rouge affichée au retour sur la page.
    dispatch("clearCancelReservationResult");

    // Réservation dont l'annulation est en cours de confirmation (null = pas de dialogue).
    const [pending, setPending] = createSignal(null);

    // La query myReservations est déjà à plat ET triée par le backend (date de
    // créneau décroissante, dates nulles en dernier) : trier ici ne réordonnerait
    // que la page courante, ce qui serait faux d'une page à l'autre.
    // myReservations vaut null à l'init et sur erreur : on ne suppose pas un tableau.
    const rows = createMemo(() => Array.isArray(state.myReservations) ? state.myReservations : []);

    const total = () => state.myReservationsTotal || 0;
    const pageCount = () => Math.max(Math.ceil(total() / RESERVATIONS_PAGE_SIZE), 1);
    const currentPage = () => Math.floor((state.myReservationsSkip || 0) / RESERVATIONS_PAGE_SIZE) + 1;
    const goToPage = (page) => {
        dispatch("loadMyReservations", { skip: (page - 1) * RESERVATIONS_PAGE_SIZE, refetch: true });
    };

    const isLoading = () => state.loading.currentUser || state.loading.myReservations;

    const askCancel = (reservation) => {
        dispatch("clearCancelReservationResult");
        setPending(reservation);
    };

    const dismiss = () => setPending(null);

    // Rien n'est envoyé tant que le client n'a pas confirmé.
    // dispatch() de storeon ne renvoie pas de promesse : inutile de l'attendre,
    // le rechargement de la liste est piloté par le store.
    const confirmCancel = () => {
        const reservation = pending();
        if (!reservation) {
            return;
        }
        setPending(null);
        dispatch("cancelReservation", reservation.orderLineId);
    };

    const cancelError = () => cancelErrorMessage(state.cancelReservationResult);
    const waitingEntries = () => Array.isArray(state.myWaitingListEntries) ? state.myWaitingListEntries : [];
    const retryWaitingList = () => {
        dispatch("clearMyWaitingListEntries");
        dispatch("loadMyWaitingListEntries", { userId: state.currentUser && state.currentUser.id });
    };

    return (
        <div class="reservations">
            <h2 class="mb-4">Mes réservations</h2>
            <Show when={state.magicLinkError}>
                <p class="text-danger" role="alert">{state.magicLinkError}</p>
            </Show>
            <Show when={cancelError()}>
                <p class="text-danger" role="alert">{cancelError()}</p>
            </Show>
            <Switch>
                <Match when={isLoading()}>
                    <p>Chargement de vos réservations…</p>
                </Match>
                <Match when={!state.currentUser}>
                    <p>Connectez-vous pour consulter vos réservations. <Link href="/user/login">Se connecter</Link></p>
                </Match>
                <Match when={state.myReservationsError}>
                    <p class="text-danger" role="alert">Une erreur est survenue lors du chargement de vos réservations. Veuillez réessayer plus tard.</p>
                </Match>
                <Match when={rows().length === 0}>
                    <p>Vous n'avez aucune réservation à venir ou datant des deux dernières semaines.</p>
                </Match>
                <Match when={rows().length > 0}>
                    <ul class="list-unstyled">
                        <For each={rows()}>
                            {(row) => {
                                const status = reservationStatus(row);
                                const horaire = formatHoraire(row);
                                const activityName = row.activityName || "Activité";
                                const assetSource = row.featuredAsset && row.featuredAsset.source;
                                return (
                                    <li class="d-flex align-items-center border-top py-3">
                                        <div
                                            class="me-3 rounded flex-shrink-0"
                                            style={`width: 64px; height: 64px; background-repeat: no-repeat; background-position: center; background-image: url(${assetSource || "/images/logo_ccpl.jpg"}); background-size: ${assetSource ? "cover" : "90%"}`}
                                        ></div>
                                        <div class="flex-fill">
                                            <div class="fw-bold">{activityName}</div>
                                            <div>{formatDate(row)}{horaire ? " · " + horaire : ""}</div>
                                            <div>{row.quantity} place{row.quantity > 1 ? "s" : ""}</div>
                                            <div class="text-muted small">Réf. {row.orderCode}</div>
                                        </div>
                                        <span
                                            class={"badge bg-" + status.variant + (status.variant === "warning" ? " text-dark" : "")}
                                            aria-label={status.ariaLabel}
                                        >{status.label}</span>
                                        <Show when={row.cancelable}>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                class="ms-3"
                                                disabled={!!state.cancellingReservation}
                                                aria-label={"Annuler la réservation : " + describe(row) + " · Réf. " + row.orderCode}
                                                onClick={() => askCancel(row)}
                                            >Annuler</Button>
                                        </Show>
                                    </li>
                                );
                            }}
                        </For>
                    </ul>
                    <Show when={pageCount() > 1}>
                        <nav class="d-flex align-items-center justify-content-between mt-3" aria-label="Pagination des réservations">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={currentPage() <= 1}
                                onClick={() => goToPage(currentPage() - 1)}
                            >Précédent</Button>
                            <span class="text-muted small" aria-live="polite">
                                Page {currentPage()} sur {pageCount()} · {total()} réservation{total() > 1 ? "s" : ""}
                            </span>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={currentPage() >= pageCount()}
                                onClick={() => goToPage(currentPage() + 1)}
                            >Suivant</Button>
                        </nav>
                    </Show>
                </Match>
            </Switch>

            <section class="mt-5" aria-labelledby="waiting-list-title">
                <h2 id="waiting-list-title" class="mb-4">Mes listes d’attente</h2>
                <Switch fallback={<p aria-live="polite">Chargement de vos listes d’attente…</p>}>
                    <Match when={state.loading.currentUser || state.loading.myWaitingListEntries}>
                        <p aria-live="polite">Chargement de vos listes d’attente…</p>
                    </Match>
                    <Match when={!state.currentUser}>
                        <p>Connectez-vous pour consulter vos listes d’attente.</p>
                    </Match>
                    <Match when={state.myWaitingListEntriesError}>
                        <div>
                            <p class="text-danger mb-2" role="alert">Une erreur est survenue lors du chargement de vos listes d’attente.</p>
                            <Button variant="outline-secondary" size="sm" onClick={retryWaitingList}>Réessayer</Button>
                        </div>
                    </Match>
                    <Match when={waitingEntries().length === 0}>
                        <p aria-live="polite">Vous n’avez aucune inscription active en liste d’attente.</p>
                    </Match>
                    <Match when={waitingEntries().length > 0}>
                        <ul class="list-unstyled">
                            <For each={waitingEntries()}>
                                {(entry) => {
                                    const horaire = formatHoraire(entry);
                                    return (
                                        <li class="d-flex flex-wrap align-items-center justify-content-between border-top py-3">
                                            <div class="me-3">
                                                <div class="fw-bold">{entry.activityName || "Activité"}</div>
                                                <div>{formatDate(entry)}{horaire ? " · " + horaire : " · —"}</div>
                                                <div>{entry.quantity} place{entry.quantity > 1 ? "s" : ""}</div>
                                            </div>
                                            <span class="badge bg-warning text-dark" aria-label="Statut : En liste d’attente">En liste d’attente</span>
                                        </li>
                                    );
                                }}
                            </For>
                        </ul>
                    </Match>
                </Switch>
            </section>
            <Modal
                show={!!pending()}
                onHide={dismiss}
                centered
                backdrop="static"
                aria-labelledby="cancel-reservation-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="cancel-reservation-title">Annuler cette réservation ?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Show when={pending()}>
                        <p class="fw-bold mb-1">{describe(pending())}</p>
                        <p class="text-muted small mb-3">Réf. {pending().orderCode}</p>
                    </Show>
                    <p class="mb-0">Cette action est définitive : la réservation ne pourra pas être rétablie.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={dismiss}>Conserver</Button>
                    <Button variant="danger" onClick={confirmCancel}>Annuler la réservation</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
